import { useState, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { ArrowLeft, MagnifyingGlass } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import api from '../../api/api'

import { useOfflineMode } from './hooks/useOfflineMode'
import { anonymizeName } from './utils'

const SLOW_CONNECTION_TIMEOUT = 5000 // 5 seconds

const ResidentSelection = ({ onSelect, onBack, packageNumber, totalPackages }) => {
  const { t } = useTranslation(['package-room', 'common'])
  const [search, setSearch] = useState('')
  const [allResidents, setAllResidents] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const {
    isConnectionIssue,
    cachedResidents,
    cachedSettings,
    handleCacheResidents,
    setHasBadConnection,
    isRoneResidencesBuilding,
  } = useOfflineMode()

  const requestTimeoutRef = useRef(null)

  const fetchResidents = () => {
    setLoading(true)
    setError(null)

    // Detect slow uploads and turn on semi-offline mode
    requestTimeoutRef.current = setTimeout(() => {
      setHasBadConnection(true)
      setLoading(false)

      // Use cached residents if available due to timeout
      if (cachedResidents.length > 0) {
        setAllResidents(cachedResidents)
      } else {
        setError(t('package-room:residentSelection.unableToLoadOffline'))
      }
    }, SLOW_CONNECTION_TIMEOUT)

    api
      .findPackageRoomResidents()
      .then((res) => {
        clearTimeout(requestTimeoutRef.current)
        const residents = res.data || []
        setAllResidents(residents)
        // Cache the fetched residents for offline use
        if (residents.length > 0) {
          handleCacheResidents(residents)
        }
        setLoading(false)
      })
      .catch(() => {
        clearTimeout(requestTimeoutRef.current)
        // If we have cached residents, use them
        if (cachedResidents.length > 0) {
          setAllResidents(cachedResidents)
        } else {
          setError(t('package-room:residentSelection.unableToLoad'))
        }
        setLoading(false)
      })
  }

  useEffect(() => {
    fetchResidents()
    return () => {
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current)
      }
    }
  }, [])

  const filteredResidents = useMemo(() => {
    // Don't show results until 3 characters are entered
    if (!isRoneResidencesBuilding && search.trim().length < 3) return []

    const searchTermLower = search.toLowerCase()
    const residentsToSearch = isConnectionIssue ? cachedResidents : allResidents

    // Note: For Rone residences, we search by unit number only
    if (isRoneResidencesBuilding) {
      return residentsToSearch.filter((resident) =>
        resident.apartment.toLowerCase().includes(searchTermLower),
      )
    }

    return residentsToSearch.filter(
      (resident) =>
        resident.fullName.toLowerCase().includes(searchTermLower) ||
        resident.apartment.toLowerCase().includes(searchTermLower),
    )
  }, [search, allResidents, cachedResidents, isConnectionIssue])

  const handleSearchChange = (e) => {
    setSearch(e.target.value)
  }

  const renderResidents = () => {
    if (!isRoneResidencesBuilding && search.length < 3) {
      return (
        <div className="package-room__no-results">
          {isRoneResidencesBuilding
            ? t('package-room:residentSelection.searchPromptUnit')
            : t('package-room:residentSelection.searchPromptResident')}
        </div>
      )
    }

    if (loading) {
      return (
        <div className="package-room__loading">{t('package-room:residentSelection.loading')}</div>
      )
    }

    if (error) {
      return <div className="package-room__error">{error}</div>
    }

    if (filteredResidents.length === 0) {
      return (
        <div className="package-room__no-results">
          {isRoneResidencesBuilding
            ? t('package-room:residentSelection.noUnitsFound')
            : t('package-room:residentSelection.noResidentsFound')}
        </div>
      )
    }

    // Note: For Rone residences, we show units only
    if (isRoneResidencesBuilding) {
      return filteredResidents.map((resident) => (
        <button
          key={resident.id}
          className="package-room__resident-item"
          onClick={() => onSelect(resident)}
        >
          <span className="package-room__resident-name">{resident.apartment}</span>
        </button>
      ))
    }

    return filteredResidents.map((resident) => (
      <button
        key={resident.id}
        className="package-room__resident-item"
        onClick={() => onSelect(resident)}
      >
        <span className="package-room__resident-name">
          {cachedSettings?.maskUsernameEnabled
            ? anonymizeName(resident.fullName)
            : resident.fullName}
        </span>
        <span className="package-room__resident-unit">{resident.apartment}</span>
      </button>
    ))
  }

  return (
    <div className="package-room__content package-room__content--full">
      <div className="package-room__navigation">
        <button className="package-room__back" onClick={onBack}>
          <ArrowLeft size={24} />
          {t('common:actions.back')}
        </button>
        <div className="package-room__progress">
          {t('package-room:progress.packageOf', { number: packageNumber, total: totalPackages })}
        </div>
      </div>

      <h2 className="package-room__title package-room__title--residents">
        {isRoneResidencesBuilding
          ? t('package-room:residentSelection.titleUnit')
          : t('package-room:residentSelection.titleResident')}
      </h2>

      <div className="package-room__search">
        <MagnifyingGlass size={24} className="package-room__search-icon" />
        <input
          type="text"
          value={search}
          onChange={handleSearchChange}
          placeholder={
            isRoneResidencesBuilding
              ? t('package-room:residentSelection.searchPlaceholderUnit')
              : t('package-room:residentSelection.searchPlaceholderResident')
          }
          className="package-room__search-input"
        />
      </div>

      <div className="package-room__residents-list">{renderResidents()}</div>
    </div>
  )
}

ResidentSelection.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  packageNumber: PropTypes.number.isRequired,
  totalPackages: PropTypes.number.isRequired,
}

export default ResidentSelection
